import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { History, Phone, MessageCircle, Instagram, Send, RotateCcw, CheckCircle2 } from "lucide-react";
import { getConversations, reopenConversation } from "@/lib/dentiumApi";

const CH_ICON = { whatsapp: MessageCircle, instagram: Instagram, telegram: Send };
const CH_COLOR = { whatsapp: "#25D366", instagram: "#E1306C", telegram: "#2AABEE" };

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export const AttendedHistory = () => {
  const qc = useQueryClient();
  const { data: conversations = [] } = useQuery({
    queryKey: ["conversations", "todos", "resuelta"],
    queryFn: () => getConversations("todos", "resuelta"),
    refetchInterval: 5000,
  });

  const attended = conversations
    .filter((c) => c.lead)
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

  const reopen = useMutation({
    mutationFn: reopenConversation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success("Llamada reprogramada", {
        description: "Vuelve a los turnos pendientes para contactar de nuevo.",
      });
    },
  });

  return (
    <div className="rounded-2xl bg-white border border-[#E7E2DA] p-4 sm:p-5" data-testid="attended-history-panel">
      <div className="flex items-center gap-2 mb-4">
        <span className="h-9 w-9 rounded-xl bg-[#F1EBE1] flex items-center justify-center">
          <History className="h-4.5 w-4.5 text-[#1A1A1A]" />
        </span>
        <div>
          <h2 className="text-base font-medium text-[#1A1A1A]">Historial de llamadas atendidas</h2>
          <p className="text-xs text-[#8A7E70]">Contactos ya gestionados por el equipo, con su fecha</p>
        </div>
      </div>

      <div className="space-y-2 max-h-[320px] overflow-y-auto dentium-scroll pr-1">
        {attended.length === 0 && (
          <div className="py-8 text-center text-xs text-[#A79E90]">Aún no hay llamadas atendidas.</div>
        )}
        {attended.map((c) => {
          const Icon = CH_ICON[c.channel];
          return (
            <div
              key={c.id}
              data-testid="attended-item"
              className="flex items-center gap-3 rounded-xl border border-[#EDE7DE] bg-[#FAF8F5]/60 px-3 py-2.5"
            >
              <span
                className="h-8 w-8 rounded-full flex items-center justify-center text-white shrink-0"
                style={{ background: CH_COLOR[c.channel] }}
              >
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#1A1A1A] truncate flex items-center gap-1.5">
                  {c.lead?.name}
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                </div>
                <div className="text-[11px] text-[#8A7E70] flex items-center gap-2 mt-0.5">
                  <span className="flex items-center gap-1"><Phone className="h-2.5 w-2.5" /> {c.lead?.phone}</span>
                  <span className="text-[#B8ADA0]">·</span>
                  <span>{c.topic}</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] text-[#A79E90] mb-1">Atendida<br />{fmtDate(c.updated_at)}</div>
                <button
                  data-testid="btn-reopen-lead"
                  onClick={() => reopen.mutate(c.id)}
                  disabled={reopen.isPending}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-[#1A1A1A] border border-[#D9CFC2] rounded-full px-2.5 py-1 hover:bg-[#1A1A1A] hover:text-white transition-colors disabled:opacity-50"
                >
                  <RotateCcw className="h-3 w-3" /> Reprogramar
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
